import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";
import "./App.css";
import { auth } from "./firebase/firebase";
import { ToastProvider } from "./components/Toast";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPendingPage from "./pages/VerifyEmailPendingPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import NotFoundPage from "./pages/NotFoundPage";
import MetricsPage from "./pages/MetricsPage";
import LangfuseDashboardPage from "./pages/LangfuseDashboardPage";
import AgentsPage from "./pages/AgentsPage";
import DiagnosticsPage from "./pages/DiagnosticsPage";
import RunsPage from "./pages/RunsPage";
import RunLaunchPage from "./pages/RunLaunchPage";
import EvaluateCasesPage from "./pages/EvaluateCasesPage";
import SchedulerPage from "./pages/SchedulerPage";
import DemoWalkthroughRedirect from "./pages/DemoWalkthroughRedirect";
import { authApi, type AppRole } from "./services/authApi";
import { readCachedRole, writeCachedRole } from "./services/roleCache";

const REQUIRE_EMAIL_VERIFICATION =
  (process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION || "true").toLowerCase() !==
  "false";
const EMAIL_VERIFICATION_BYPASS_IDS = new Set(
  (process.env.REACT_APP_EMAIL_VERIFICATION_BYPASS_IDS || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
);

const AuthCheckingState: React.FC = () => (
  <div
    className="auth-checking-state"
    role="status"
    aria-live="polite"
    aria-busy="true"
    aria-label="Checking authentication"
  >
    <div className="auth-checking-spinner" />
  </div>
);

function useAuthUser(): { user: User | null; checking: boolean } {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setChecking(false);
    });
    return () => unsub();
  }, []);

  return { user, checking };
}

function useAppRole(user: User | null): { role: AppRole | null; loading: boolean } {
  const [state, setState] = useState<{
    uid: string | null;
    role: AppRole | null;
    loading: boolean;
  }>({ uid: null, role: null, loading: false });

  useEffect(() => {
    let mounted = true;
    if (!user) {
      setState({ uid: null, role: null, loading: false });
      return;
    }
    const uid = user.uid;
    const cachedRole = readCachedRole(uid);
    setState({ uid, role: cachedRole, loading: cachedRole === null });
    void authApi
      .getMe()
      .then((me) => {
        if (!mounted) return;
        writeCachedRole(uid, me.role);
        setState({ uid, role: me.role, loading: false });
      })
      .catch(() => {
        if (mounted) {
          setState({ uid, role: cachedRole ?? "admin", loading: false });
        }
      });
    return () => {
      mounted = false;
    };
  }, [user]);

  if (user && state.uid !== user.uid) {
    const cachedRole = readCachedRole(user.uid);
    return { role: cachedRole, loading: cachedRole === null };
  }
  return { role: state.role, loading: state.loading };
}

const AuthenticatedLayout: React.FC<{
  user: User | null;
  checking: boolean;
  role: AppRole | null;
}> = ({ user, checking, role }) => {
  if (checking) return <AuthCheckingState />;
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const isPasswordUser = user.providerData?.some((p) => p?.providerId === "password");
  const bypassByUid = EMAIL_VERIFICATION_BYPASS_IDS.has((user.uid || "").toLowerCase());
  const bypassByEmail = EMAIL_VERIFICATION_BYPASS_IDS.has(
    (user.email || "").toLowerCase()
  );
  const isBypassed = bypassByUid || bypassByEmail;
  if (
    REQUIRE_EMAIL_VERIFICATION &&
    !isBypassed &&
    isPasswordUser &&
    !user.emailVerified
  ) {
    return <Navigate to="/verify-email-pending" replace />;
  }

  return <AppShell currentUser={user} role={role} />;
};

const SuperadminRoute: React.FC<{
  children: React.ReactElement;
  role: AppRole | null;
  loading: boolean;
}> = ({ children, role, loading }) => {
  const location = useLocation();

  if (loading) return <AuthCheckingState />;
  if (role !== "superadmin") {
    return <Navigate to="/agents" replace state={{ from: location.pathname }} />;
  }
  return children;
};

const ProtectedRoutes: React.FC = () => {
  const { user, checking } = useAuthUser();
  const { role, loading: roleLoading } = useAppRole(user);

  return (
    <Routes>
      <Route
        path="/"
        element={<AuthenticatedLayout user={user} checking={checking} role={role} />}
      >
        <Route index element={<Navigate to="/agents" replace />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route
          path="run-launch"
          element={
            <SuperadminRoute role={role} loading={roleLoading}>
              <RunLaunchPage />
            </SuperadminRoute>
          }
        />
        <Route path="evaluate" element={<EvaluateCasesPage />} />
        <Route path="runs" element={<RunsPage />} />
        <Route
          path="diagnostics"
          element={
            <SuperadminRoute role={role} loading={roleLoading}>
              <DiagnosticsPage />
            </SuperadminRoute>
          }
        />
        <Route
          path="scheduler"
          element={
            <SuperadminRoute role={role} loading={roleLoading}>
              <SchedulerPage />
            </SuperadminRoute>
          }
        />
        <Route path="dashboard" element={<LangfuseDashboardPage />} />
        <Route
          path="metrics"
          element={
            <SuperadminRoute role={role} loading={roleLoading}>
              <MetricsPage />
            </SuperadminRoute>
          }
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const PublicOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, checking } = useAuthUser();

  if (checking) return <AuthCheckingState />;
  if (user) return <Navigate to="/agents" replace />;
  return children;
};

function App() {
  const routerBaseName = process.env.PUBLIC_URL || "/";

  return (
    <ToastProvider>
      <Router basename={routerBaseName}>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicOnlyRoute>
                <SignupPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnlyRoute>
                <ForgotPasswordPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicOnlyRoute>
                <ResetPasswordPage />
              </PublicOnlyRoute>
            }
          />
          <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/demo/dashboard" element={<DemoWalkthroughRedirect />} />
          <Route path="*" element={<ProtectedRoutes />} />
        </Routes>
      </Router>
    </ToastProvider>
  );
}

export default App;
